import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';


/**
* Cart Recommendations Carousel Component
* Manages horizontal scrolling of recommended products and adding items to cart
*/
class CartRecommendationsComponent extends Component {
 connectedCallback() {
   try {
     // Éviter la double initialisation
     if (this._initialized) {
       console.log('Component already initialized, skipping...');
       return;
     }
     this._initialized = true;

     super.connectedCallback();
     this.track = this.querySelector('[ref="track"]');
     this.prevButton = this.querySelector('[ref="prevButton"]');
     this.nextButton = this.querySelector('[ref="nextButton"]');
     this.itemsContainer = this.querySelector('[ref="itemsContainer"]');
     this.scrollAmount = 200; // pixels to scroll per click

     const productId = this.dataset.productId;
     const sectionId = this.dataset.sectionId || 'cart-drawer';
     this.collectionHandle = this.dataset.collectionHandle || '';

     console.log('Cart recommendations initialized', {
       productId,
       sectionId,
       collectionHandle: this.collectionHandle,
       track: !!this.track,
       prevButton: !!this.prevButton,
       nextButton: !!this.nextButton,
       itemsContainer: !!this.itemsContainer
     });

     // Charger les produits depuis la collection ou les recommandations
     if (this.collectionHandle) {
       this.loadCollectionProducts(this.collectionHandle);
     } else if (productId) {
       this.loadRecommendations(productId, sectionId);
     }

     // Écouter les événements de mise à jour du panier pour recharger les recommandations
     this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
     document.addEventListener(ThemeEvents.cartAdd, this.boundHandleCartUpdate);
     document.addEventListener(ThemeEvents.cartUpdate, this.boundHandleCartUpdate);

     // Ajouter event listeners pour les boutons flèches
     if (this.prevButton) {
       this.prevButton.addEventListener('click', () => {
         console.log('Prev button clicked');
         this.scrollPrev();
       });
     }

     if (this.nextButton) {
       this.nextButton.addEventListener('click', () => {
         console.log('Next button clicked');
         this.scrollNext();
       });
     }
   } catch (error) {
     console.error('Error initializing cart recommendations:', error);
   }
 }

 /**
  * Réinitialise le composant (pour forcer le chargement après morphing)
  */
 reinitialize() {
   console.log('Reinitializing cart recommendations component');
   this._initialized = false;
   this.connectedCallback();
 }

 disconnectedCallback() {
   // Nettoyer les event listeners quand le composant est retiré
   if (this.boundHandleCartUpdate) {
     document.removeEventListener(ThemeEvents.cartAdd, this.boundHandleCartUpdate);
     document.removeEventListener(ThemeEvents.cartUpdate, this.boundHandleCartUpdate);
   }
 }

 /**
  * Gère les événements de mise à jour du panier
  * @param {CartAddEvent | CartUpdateEvent} event
  */
 async handleCartUpdate(event) {
   try {
     console.log('Cart updated, reloading recommendations', event);

     // Récupérer le panier mis à jour pour obtenir l'ID du premier produit
     const rootUrl = window.Shopify?.routes?.root || '/';
     const cartResponse = await fetch(`${rootUrl}cart.js`);
     if (!cartResponse.ok) {
       throw new Error('Failed to fetch cart');
     }

     const cart = await cartResponse.json();
     console.log('Cart data:', cart);

     if (cart.items && cart.items.length > 0) {
       const firstProductId = cart.items[0].product_id;
       console.log('First product ID:', firstProductId);

       // Mettre à jour l'attribut data
       this.dataset.productId = firstProductId;

       // Afficher le composant au cas où il était masqué
       this.style.display = '';

       // Recharger depuis la collection ou les recommandations
       if (this.collectionHandle) {
         await this.loadCollectionProducts(this.collectionHandle);
       } else {
         await this.loadRecommendations(firstProductId, this.dataset.sectionId || 'cart-drawer');
       }
     } else {
       // Si le panier est vide, masquer le composant
       this.style.display = 'none';
     }
   } catch (error) {
     console.error('Error handling cart update:', error);
   }
 }

 /**
  * Charge les recommandations de produits depuis l'API Shopify
  * @param {string} productId - L'ID du produit pour les recommandations
  * @param {string} sectionId - L'ID de la section
  */
 async loadRecommendations(productId, sectionId) {
   try {
     const rootUrl = window.Shopify?.routes?.root || '/';
     const url = `${rootUrl}recommendations/products.json?product_id=${productId}&limit=8&intent=related`;
     console.log('Loading recommendations from:', url);

     const response = await fetch(url);
     if (!response.ok) {
       throw new Error(`HTTP error! status: ${response.status}`);
     }

     const data = await response.json();
     const products = data.products || [];

     console.log('Recommendations loaded:', products.length, products);

     if (products.length > 0 && this.itemsContainer) {
       // Récupérer les IDs des produits déjà dans le panier
       const cartProductIds = await this.getCartProductIds();
       console.log('Cart product IDs:', cartProductIds);

       // Filtrer les produits qui ne sont pas déjà dans le panier
       const filteredProducts = products.filter(product => !cartProductIds.includes(product.id));
       console.log('Filtered products:', filteredProducts.length, filteredProducts);

       if (filteredProducts.length > 0) {
         // Vider le conteneur et ajouter les nouveaux produits
         this.itemsContainer.innerHTML = '';
         console.log('Adding products to container...');

         filteredProducts.forEach(product => {
           const productHtml = this.createProductCard(product);
           this.itemsContainer.appendChild(productHtml);
         });

         console.log('Products added, container children:', this.itemsContainer.children.length);

         // Ajouter les event listeners pour les boutons +
         this.querySelectorAll('.cart-recommendations__add-btn').forEach(button => {
           button.addEventListener('click', (e) => this.addToCart(e));
         });

         // S'assurer que le composant est visible
         this.style.display = '';
       } else {
         console.log('No products after filtering, hiding component');
         // Si aucune recommandation après filtrage, masquer le composant
         this.style.display = 'none';
       }
     } else {
       console.log('No products or no container, hiding component');
       // Si aucune recommandation, masquer le composant
       this.style.display = 'none';
     }
   } catch (error) {
     console.error('Error loading recommendations:', error);
     // Masquer le composant en cas d'erreur
     this.style.display = 'none';
   }
 }

 /**
  * Charge les produits depuis une collection spécifique
  * @param {string} collectionHandle - Le handle de la collection
  */
 async loadCollectionProducts(collectionHandle) {
   try {
     const rootUrl = window.Shopify?.routes?.root || '/';
     const url = `${rootUrl}collections/${collectionHandle}/products.json?limit=20`;
     console.log('Loading collection products from:', url);

     const response = await fetch(url);
     if (!response.ok) {
       throw new Error(`HTTP error! status: ${response.status}`);
     }

     const data = await response.json();
     const products = data.products || [];

     console.log('Collection products loaded:', products.length);

     if (products.length > 0 && this.itemsContainer) {
       // Récupérer les IDs des produits déjà dans le panier
       const cartProductIds = await this.getCartProductIds();
       console.log('Cart product IDs:', cartProductIds);

       // Filtrer les produits qui ne sont pas déjà dans le panier
       const filteredProducts = products.filter(product => !cartProductIds.includes(product.id));
       console.log('Filtered collection products:', filteredProducts.length);

       if (filteredProducts.length > 0) {
         this.itemsContainer.innerHTML = '';

         filteredProducts.forEach(product => {
           // Adapter le format de la collection API au format attendu par createProductCard
           // L'API collection retourne les prix en string (ex: "29.00"), convertir en centimes
           const variantPrice = parseFloat(product.variants?.[0]?.price || '0') * 100;
           const adaptedVariants = product.variants?.map(v => ({
             ...v,
             price: parseFloat(v.price || '0') * 100,
             compare_at_price: v.compare_at_price ? parseFloat(v.compare_at_price) * 100 : null
           }));
           const adaptedProduct = {
             id: product.id,
             title: product.title,
             handle: product.handle,
             featured_image: product.images?.[0]?.src || product.image?.src || null,
             variants: adaptedVariants,
             price: variantPrice
           };
           const productHtml = this.createProductCard(adaptedProduct);
           this.itemsContainer.appendChild(productHtml);
         });

         // Ajouter les event listeners pour les boutons +
         this.querySelectorAll('.cart-recommendations__add-btn').forEach(button => {
           button.addEventListener('click', (e) => this.addToCart(e));
         });

         this.style.display = '';
       } else {
         this.style.display = 'none';
       }
     } else {
       this.style.display = 'none';
     }
   } catch (error) {
     console.error('Error loading collection products:', error);
     this.style.display = 'none';
   }
 }

 /**
  * Récupère les IDs des produits déjà dans le panier
  * @returns {Promise<Array<number>>} - Les IDs des produits dans le panier
  */
 async getCartProductIds() {
   try {
     // Récupérer le panier via l'API Shopify
     const rootUrl = window.Shopify?.routes?.root || '/';
     const response = await fetch(`${rootUrl}cart.js`);
     if (!response.ok) {
       throw new Error('Failed to fetch cart');
     }

     const cart = await response.json();

     // Extraire les IDs des produits
     const productIds = cart.items.map(item => item.product_id);

     return productIds;
   } catch (error) {
     console.error('Error getting cart product IDs:', error);
     return [];
   }
 }

 /**
  * Crée la carte HTML d'un produit
  * @param {Object} product - Les données du produit
  * @returns {HTMLElement} - L'élément HTML de la carte produit
  */
 createProductCard(product) {
   const item = document.createElement('div');
   item.className = 'cart-recommendations__item';

   const variant = product.variants?.[0];
   const imageUrl = product.featured_image || product.images?.[0];
   const price = this.formatMoney(variant?.price || product.price);
   const comparePrice = variant?.compare_at_price;
   const priceVaries = product.price_min !== product.price_max;

   const rootUrl = window.Shopify?.routes?.root || '/';
   item.innerHTML = `
     <div class="cart-recommendations__card">
       <a href="${rootUrl}products/${product.handle}" class="cart-recommendations__link">
         <div class="cart-recommendations__image-wrapper">
           ${imageUrl ? `
             <img
               src="${imageUrl}"
               alt="${this.escapeHtml(product.title)}"
               loading="lazy"
               width="200"
               height="200"
               class="cart-recommendations__image"
             >
           ` : `
             <div class="cart-recommendations__no-image">
               <svg viewBox="0 0 525.5 525.5" xmlns="http://www.w3.org/2000/svg">
                 <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path>
                 <path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5 5.4h-68.9c9.4-4.5 26.1-10.4 32.4-12.2l11.8 11.8c.4.4.9.6 1.4.6s1-.2 1.4-.6c.8-.8.8-2 0-2.8l-13.5-13.5c-.8-.8-2-.8-2.8 0-.8.8-.8 2 0 2.8l1.5 1.5-7.1 2.2c-1.5.5-3.5 1.1-5.8 1.8l-25.3-25.3c-2.4-2.4-5.6-3.7-8.9-3.7s-6.5 1.3-8.9 3.7l-18.2 18.2V225h101.5v72.7z"></path>
                 <path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6 0 3.6-2.9 6.6-6.6 6.6-3.6 0-6.6-2.9-6.6-6.6-.1-3.7 3-6.6 6.6-6.6z"></path>
               </svg>
             </div>
           `}
         </div>
         <div class="cart-recommendations__info">
           <p class="cart-recommendations__product-title">${this.escapeHtml(product.title)}</p>
           <p class="cart-recommendations__price">
             ${price}
           </p>
         </div>
       </a>
       <button
         type="button"
         class="cart-recommendations__add-btn"
         aria-label="Ajouter ${this.escapeHtml(product.title)} au panier"
         data-variant-id="${variant?.id || ''}"
       >
         <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="cart-recommendations__add-icon">
           <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
         </svg>
         <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="cart-recommendations__check-icon" style="display: none;">
           <path d="M2 7L6 11L12 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
         </svg>
       </button>
     </div>
   `;

   return item;
 }

 /**
  * Formate un prix en centimes en format monétaire
  * @param {number} cents - Le prix en centimes
  * @returns {string} - Le prix formaté
  */
 formatMoney(cents) {
   if (typeof cents === 'undefined' || cents === null) return '€0,00';
   const euros = (cents / 100).toFixed(2).replace('.', ',');
   return `€${euros}`;
 }

 /**
  * Échappe les caractères HTML pour éviter les injections XSS
  * @param {string} text - Le texte à échapper
  * @returns {string} - Le texte échappé
  */
 escapeHtml(text) {
   const div = document.createElement('div');
   div.textContent = text;
   return div.innerHTML;
 }


 scrollPrev() {
   if (this.track) {
     this.track.scrollBy({
       left: -this.scrollAmount,
       behavior: 'smooth'
     });
   }
 }


 scrollNext() {
   if (this.track) {
     this.track.scrollBy({
       left: this.scrollAmount,
       behavior: 'smooth'
     });
   }
 }


 /**
  * Adds a product to the cart
  * @param {Event & {currentTarget: HTMLButtonElement}} event - The click event
  */
 async addToCart(event) {
   event.preventDefault();
   event.stopPropagation();


   const button = event.currentTarget;
   const variantId = button.dataset.variantId;


   console.log('Add to cart clicked', { variantId, disabled: button.disabled });


   if (!variantId || button.disabled) return;


   // Disable button to prevent double clicks
   button.disabled = true;


   // Change to checkmark immediately for visual feedback
   const addIcon = button.querySelector('.cart-recommendations__add-icon');
   const checkIcon = button.querySelector('.cart-recommendations__check-icon');


   if (addIcon) addIcon.style.display = 'none';
   if (checkIcon) checkIcon.style.display = 'block';


   try {
     // Prepare form data
     const formData = new FormData();
     formData.append('id', variantId);
     formData.append('quantity', '1');


     // Get section IDs for cart update
     const cartItemsComponents = document.querySelectorAll('cart-items-component');
     const sectionIds = [];
     cartItemsComponents.forEach((item) => {
       if (item instanceof HTMLElement && item.dataset.sectionId) {
         sectionIds.push(item.dataset.sectionId);
       }
     });


     if (sectionIds.length > 0) {
       formData.append('sections', sectionIds.join(','));
     }


     // Add to cart
     const cartUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js';
     console.log('Fetching cart URL:', cartUrl);


     const response = await fetch(cartUrl, {
       method: 'POST',
       headers: {
         'X-Requested-With': 'XMLHttpRequest'
       },
       body: formData
     });


     const data = await response.json();
     console.log('Cart response:', { ok: response.ok, data });


     if (response.ok) {
       // Dispatch CartAddEvent to update the cart
       document.dispatchEvent(
         new CartAddEvent({}, 'cart-recommendations', {
           source: 'cart-recommendations',
           itemCount: 1,
           sections: data.sections
         })
       );


       // Keep checkmark for 2 seconds then revert to +
       setTimeout(() => {
         if (addIcon) addIcon.style.display = 'block';
         if (checkIcon) checkIcon.style.display = 'none';
         button.disabled = false;
       }, 2000);
     } else {
       // Handle error - revert icon immediately
       if (addIcon) addIcon.style.display = 'block';
       if (checkIcon) checkIcon.style.display = 'none';
       button.disabled = false;
       console.error('Error adding to cart:', data);
       alert(data.message || 'Impossible d\'ajouter le produit au panier');
     }
   } catch (error) {
     // Handle error - revert icon immediately
     if (addIcon) addIcon.style.display = 'block';
     if (checkIcon) checkIcon.style.display = 'none';
     button.disabled = false;
     console.error('Error adding to cart:', error);
     alert('Une erreur est survenue');
   }
 }
}


// Register custom element with error handling for private browsing
try {
 if (!customElements.get('cart-recommendations-component')) {
   customElements.define('cart-recommendations-component', CartRecommendationsComponent);
 }
} catch (error) {
 console.error('Error registering cart-recommendations-component:', error);
}

// Écouter les événements de mise à jour du panier pour réinitialiser le composant
// Ceci est nécessaire car morphSection ne déclenche pas toujours connectedCallback
document.addEventListener(ThemeEvents.cartAdd, () => {
 console.log('CartAdd event detected, checking for cart recommendations component...');
 // Attendre un peu que le DOM soit mis à jour
 setTimeout(() => {
   const component = document.querySelector('cart-recommendations-component');
   if (component && !component._initialized) {
     console.log('Found uninitialized component, initializing...');
     component.connectedCallback();
   } else if (component && component._initialized) {
     console.log('Component already initialized');
   } else {
     console.log('No cart recommendations component found in DOM');
   }
 }, 100);
});

document.addEventListener(ThemeEvents.cartUpdate, () => {
 console.log('CartUpdate event detected, checking for cart recommendations component...');
 // Attendre un peu que le DOM soit mis à jour
 setTimeout(() => {
   const component = document.querySelector('cart-recommendations-component');
   if (component && !component._initialized) {
     console.log('Found uninitialized component, initializing...');
     component.connectedCallback();
   }
 }, 100);
});